import { Button, MessageBar, MessageBarBody, Spinner, Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { Add16Regular, ArrowClockwise16Regular, Edit16Regular } from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { NetworkMatrix, PolicyVersion } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { useMe } from '../auth/MeProvider';
import { NoAccess } from '../components/NoAccess';
import { PageHeader } from '../components/PageHeader';
import { formatRelative } from '../lib/format';
import { useShell } from '../lib/shell';
import { presence } from '../lib/status';
import { errorText } from '../lib/useRelay';
import { vt } from '../theme';
import { Matrix } from './networks/Matrix';
import { toGrid } from './networks/model';
import { NetworkCards } from './networks/NetworkCards';
import { NetworkDrawer } from './networks/NetworkDrawer';

const useStyles = makeStyles({
  sectionHead: { display: 'flex', alignItems: 'baseline', columnGap: '14px', flexWrap: 'wrap', flexShrink: 0 },
  sectionTitle: { margin: 0, fontSize: '14px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  sectionSub: { fontSize: '11.5px', color: vt.fg3 },
  mono: { fontFamily: tokens.fontFamilyMonospace },
  version: {
    marginLeft: 'auto',
    height: '20px',
    lineHeight: '20px',
    paddingInline: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: vt.tintNeutral,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '10.5px',
    color: vt.fg3,
  },
  versionOpen: { backgroundColor: vt.tintOrange, color: vt.orange },
  block: { display: 'flex', flexDirection: 'column', rowGap: '12px', flexShrink: 0 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: {
    height: '30px',
    textAlign: 'start',
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    color: vt.fg3,
    borderBottom: `1px solid ${vt.surface2}`,
  },
  td: { height: '38px', borderBottom: `1px solid ${vt.surface2}`, color: vt.fg2, paddingRight: '12px' },
  end: { textAlign: 'end', paddingRight: 0 },
  name: { color: vt.fg1, fontWeight: tokens.fontWeightSemibold, fontSize: '13px' },
  dot: { display: 'block', width: '7px', height: '7px', borderRadius: '50%', backgroundColor: vt.fg4 },
  online: { backgroundColor: vt.green },
  tags: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  tag: {
    height: '18px',
    lineHeight: '18px',
    paddingInline: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: vt.surface3,
    border: `1px solid ${vt.surface2}`,
    fontSize: '10px',
    color: vt.fg2,
  },
  quiet: { fontSize: '11.5px', color: vt.fg4 },
});

/** The matrix and the active policy (to number its rules). Loaded on open, on Refresh and after a change. */
function useMatrix() {
  const { api } = useAuth();
  const [matrix, setMatrix] = useState<NetworkMatrix | null>(null);
  const [policy, setPolicy] = useState<PolicyVersion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    const [m, p] = await Promise.allSettled([api.networkMatrix(), api.getPolicy()]);
    if (mine !== seq.current) return;
    if (m.status === 'fulfilled') setMatrix(m.value);
    // Without the policy the tooltips show rule ids instead of #N; the matrix still stands.
    if (p.status === 'fulfilled') setPolicy(p.value);
    setError(m.status === 'rejected' ? errorText(m.reason) : null);
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { matrix, policy, error, reload };
}

// This page loads no flows, so a dot is online or offline, never live.
const NO_LIVE: ReadonlySet<string> = new Set();

type Drawer = { mode: 'create' } | { mode: 'edit'; id: string } | null;

function NetworksScreen() {
  const s = useStyles();
  const shell = useShell();
  const { networks, nextFreeCidr, networksError, devices, statuses } = shell;
  const data = useMatrix();
  const [params, setParams] = useSearchParams();
  const [drawer, setDrawer] = useState<Drawer>(null);
  const [now, setNow] = useState(() => Date.now());

  // ?n=<id> (the sidebar's quick links) picks the network; otherwise the first one.
  const selectedId = networks?.some((n) => n.id === params.get('n')) ? params.get('n') : (networks?.[0]?.id ?? null);
  const selected = networks?.find((n) => n.id === selectedId) ?? null;
  const select = (id: string | null) => setParams(id ? { n: id } : {}, { replace: true });

  const grid = useMemo(() => (data.matrix ? toGrid(data.matrix, networks, data.policy) : null), [data.matrix, networks, data.policy]);
  const members = useMemo(() => (devices ?? []).filter((d) => d.network === selectedId), [devices, selectedId]);
  const statusOf = useMemo(() => new Map((statuses ?? []).map((x) => [x.deviceId, x])), [statuses]);
  const editing = drawer?.mode === 'edit' ? (networks?.find((n) => n.id === drawer.id) ?? null) : null;

  const refresh = () => {
    setNow(Date.now());
    void data.reload();
    void shell.reloadNetworks();
    void shell.reloadDevices();
    void shell.reloadStatuses();
  };

  // A new or renamed network shows up in the cards and the sidebar; isolation changes the matrix.
  const afterChange = async (selectId: string | null) => {
    await Promise.all([shell.reloadNetworks(), data.reload()]);
    setDrawer(null);
    select(selectId);
  };

  const error = networksError ?? data.error;

  return (
    <>
      <PageHeader
        title="Networks"
        actions={
          <>
            <Tooltip content="Refresh" relationship="label">
              <Button appearance="outline" shape="circular" size="small" icon={<ArrowClockwise16Regular />} onClick={refresh} />
            </Tooltip>
            <Button appearance="primary" shape="circular" icon={<Add16Regular />} onClick={() => setDrawer({ mode: 'create' })}>
              New network
            </Button>
          </>
        }
      />

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {!networks ? (
        !networksError && <Spinner label="Loading networks" />
      ) : (
        <NetworkCards networks={networks} selectedId={selectedId} onSelect={select} />
      )}

      <section className={s.block} aria-labelledby="reach-title">
        <div className={s.sectionHead}>
          <h2 id="reach-title" className={s.sectionTitle}>
            Reachability
          </h2>
          <span className={s.sectionSub}>what each network may open to each other — click a cell to edit its rules</span>
          {grid && (
            <span className={mergeClasses(s.version, grid.open && s.versionOpen)} data-testid="matrix-version">
              {grid.open ? grid.versionLabel : `active ${grid.versionLabel}`}
            </span>
          )}
        </div>
        {grid ? <Matrix grid={grid} /> : !data.error && <Spinner size="small" label="Loading the matrix" />}
      </section>

      {selected && (
        <section className={s.block} aria-labelledby="members-title">
          <div className={s.sectionHead}>
            <h2 id="members-title" className={s.sectionTitle}>
              {selected.name}
            </h2>
            <span className={mergeClasses(s.sectionSub, s.mono)}>{selected.cidr}</span>
            <span className={s.sectionSub}>
              {members.length} device{members.length === 1 ? '' : 's'}
            </span>
            <span style={{ marginLeft: 'auto' }}>
              <Button
                appearance="subtle"
                shape="circular"
                size="small"
                icon={<Edit16Regular />}
                onClick={() => setDrawer({ mode: 'edit', id: selected.id })}
              >
                Edit
              </Button>
            </span>
          </div>
          {members.length === 0 ? (
            <span className={s.quiet}>No devices in this network yet.</span>
          ) : (
            <table className={s.table} aria-label={`Devices in ${selected.name}`}>
              <thead>
                <tr>
                  <th className={s.th} style={{ width: '22px' }} aria-label="Status" />
                  <th className={s.th}>Name</th>
                  <th className={s.th}>Address</th>
                  <th className={s.th}>Tags</th>
                  <th className={mergeClasses(s.th, s.end)}>Last handshake</th>
                </tr>
              </thead>
              <tbody>
                {members.map((d) => {
                  const st = statusOf.get(d.id);
                  const p = presence(d.id, st, NO_LIVE, now);
                  return (
                    <tr key={d.id} data-device={d.id}>
                      <td className={s.td}>
                        <span
                          className={mergeClasses(s.dot, p !== 'offline' && s.online)}
                          role="img"
                          aria-label={p === 'offline' ? 'Offline' : 'Online'}
                        />
                      </td>
                      <td className={mergeClasses(s.td, s.name)}>{d.name}</td>
                      <td className={mergeClasses(s.td, s.mono)}>{d.overlayIp}</td>
                      <td className={s.td}>
                        <span className={s.tags}>
                          {d.tags.map((t) => (
                            <span key={t} className={s.tag}>
                              {t}
                            </span>
                          ))}
                        </span>
                      </td>
                      <td className={mergeClasses(s.td, s.mono, s.end)}>{formatRelative(st?.lastHandshakeAt ?? null, now)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      )}

      {drawer && (drawer.mode === 'create' || editing) && (
        <NetworkDrawer
          key={drawer.mode === 'create' ? ':new' : drawer.id}
          network={editing}
          nextFreeCidr={nextFreeCidr}
          onClose={() => setDrawer(null)}
          onDone={afterChange}
        />
      )}
    </>
  );
}

/** Mockup 04, admin only: the networks, what each may reach, and the devices in the selected one. */
export function Networks() {
  const { me, loading, error, isAdmin } = useMe();
  return (
    <>
      {loading && <Spinner label="Loading your permissions" />}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {me && !isAdmin && (
        <>
          <PageHeader title="Networks" />
          <NoAccess />
        </>
      )}
      {me && isAdmin && <NetworksScreen />}
    </>
  );
}
